import React from 'react';
import { Form, Dropdown, Message, Icon, Popup } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import './keyFallbackV107.css';
import {
  keySourceOptionsV1019,
  sourceIdentityV1019,
} from './sourceIdentityV1019';
import { KeyRetryPolicyV1021 } from './retryControlsV1021';
import useKeyCommercialOptionsV1110 from './useKeyCommercialOptionsV1110';

export function KeyHelpV107({ label, content }) {
  return (
    <Popup
      className='key-help-popup-v107'
      content={content}
      on={['hover', 'focus', 'click']}
      position='top center'
      popperModifiers={[
        {
          name: 'preventOverflow',
          enabled: true,
          options: { padding: 12, rootBoundary: 'viewport' },
        },
      ]}
      trigger={
        <button type='button' className='key-help-v107' aria-label={label}>
          <Icon name='exclamation circle' />
        </button>
      }
    />
  );
}

export function KeyGroupEditorV107({ value, onChange, targetUserId, optionsState }) {
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith('zh');
  const local = useKeyCommercialOptionsV1110(targetUserId, !optionsState);
  const options = (optionsState || local).data;
  const error = !optionsState && local.error
    ? (zh ? '无法加载 Key 分组权限' : 'Key group permissions unavailable') : '';
  const policy = value || { strategy: 'inherit' };
  if (!options?.can_restrict_groups && !policy.group_restriction_enabled) {
    return error ? <Message negative content={error} /> : null;
  }
  const groups = options?.groups || [];
  const label = zh ? '限制可用分组' : 'Restrict routing groups';
  return (
    <div className='key-group-scope-v107'>
      {error && <Message negative content={error} />}
      <div className='key-control-help-v107'>
        <Form.Checkbox
          toggle
          label={label}
          disabled={!options?.can_restrict_groups}
          checked={Boolean(policy.group_restriction_enabled)}
          onChange={(_, { checked }) =>
            onChange({ ...policy, group_restriction_enabled: checked })
          }
        />
        <KeyHelpV107
          label={zh ? '分组限制说明' : 'Group restriction help'}
          content={
            zh
              ? '仅 Root 自己的 Key 可设置。开启后，这把 Key 只能使用所选分组中的渠道；指定渠道、重试和 Source 兜底也受限制。分组限制先于模型范围，不改变组织权限或计费分组。未选择分组时不能保存；已撤销分组不会自动放行。'
              : 'For Root-owned keys only. When enabled, this key can use only channels in the selected groups, including explicit channels, retries and Source fallback. This narrows routing before model scope; it does not change organization permissions or the billing group. Select at least one group. Revoked groups never reopen access.'
          }
        />
      </div>
      {policy.group_restriction_enabled && (
        <Form.Dropdown
          label={zh ? '允许的分组' : 'Allowed groups'}
          fluid
          multiple
          search
          selection
          aria-label={zh ? '允许的分组' : 'Allowed groups'}
          value={policy.allowed_groups || []}
          options={[
            ...new Set([...groups, ...(policy.allowed_groups || [])]),
          ].map((group) => ({
            key: group,
            value: group,
            text: group,
            disabled: !groups.includes(group),
          }))}
          onChange={(_, { value: next }) =>
            onChange({ ...policy, allowed_groups: next })
          }
        />
      )}
    </div>
  );
}

export function KeyCommercialEditorV106({ value, onChange, targetUserId, optionsState }) {
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith('zh');
  const local = useKeyCommercialOptionsV1110(targetUserId, !optionsState);
  const options = (optionsState || local).data || { sources: [], models: [] };
  const error = !optionsState && local.error
    ? (zh ? '无法加载 Key 可用选项，请稍后重试' : 'Unable to load Key options. Please retry.') : '';
  const policy = value || {
    strategy: 'quality',
    source_refs: [],
    source_fallback_enabled: true,
    retry_enabled: true,
  };
  const sourceSelection = keySourceOptionsV1019(
    options.sources || [],
    policy.source_refs || []
  );
  const strategies = [
    'inherit',
    'stable',
    'cheap',
    'success',
    'speed',
    'quality',
    'official',
    'manual',
  ];
  const selectedStrategy = strategies.includes(policy.strategy)
    ? policy.strategy
    : 'quality';
  const cleanPolicy = (next) => {
    const {
      model_fallback_enabled: _legacyFallback,
      fallback_models: _legacyModels,
      offer_id: _legacyOffer,
      channel_ids: _legacyChannels,
      ...clean
    } = next;
    return clean;
  };
  const update = (name, next) =>
    onChange(cleanPolicy({ ...policy, [name]: next }));
  const selectStrategy = (next) => {
    const nextPolicy = { ...policy, strategy: next };
    if (next === 'manual') {
      nextPolicy.source_refs = policy.source_refs || [];
    } else {
      nextPolicy.source_refs = [];
    }
    onChange(cleanPolicy(nextPolicy));
  };
  return (
    <div className='commercial-key-v106'>
      {error && <Message negative content={error} />}
      <Form.Select
        label={
          <>
            {zh ? '路由策略' : 'Routing strategy'}{' '}
            <KeyHelpV107
              label={zh ? '路由策略说明' : 'Routing strategy help'}
              content={
                zh
                  ? '自动策略只决定可用 Source 的先后顺序，不因未确认全参、无压缩、官方标签或缺少健康与速度数据排除候选。优先项不可用时保留其他合格 Source。价格优先使用 OpenLux 自身可用子 Source 的第二低不同倍率（只有一个时用该值），实际收费仍以回执为准。权限、分组、模型范围、真实请求能力、价格上限和手动指定 Source 仍是限制条件。'
                  : 'Automatic strategies order eligible Sources; missing quality declarations, official labels or health and speed data do not exclude candidates. Other eligible Sources remain available when preferred ones cannot serve. Lowest price ranks OpenLux by its own second-lowest distinct eligible child multiplier, or its sole multiplier; actual charges follow receipts. Permissions, groups, model scope, actual request capabilities, price ceilings and manual Source selection remain limits.'
              }
            />
          </>
        }
        value={selectedStrategy}
        options={[
          ...(['inherit', 'stable'].includes(selectedStrategy)
            ? [
                {
                  value: selectedStrategy,
                  text:
                    selectedStrategy === 'inherit'
                      ? zh
                        ? '继承现有策略'
                        : 'Inherit current strategy'
                      : zh
                      ? '稳定且全参（旧策略）'
                      : 'Stable, full parameters (legacy)',
                },
              ]
            : []),
          { value: 'cheap', text: zh ? '价格优先' : 'Lowest price' },
          {
            value: 'success',
            text: zh ? '成功率优先' : 'Highest success rate',
          },
          { value: 'speed', text: zh ? '速度优先' : 'Fastest response' },
          {
            value: 'quality',
            text: zh
              ? '全参、无压缩及稳定性优先'
              : 'Full parameters, uncompressed, stable',
          },
          { value: 'official', text: zh ? '官方优先' : 'Official preferred' },
          { value: 'manual', text: zh ? '指定 Source' : 'Specified Source' },
        ].map((item) => ({ ...item, key: item.value }))}
        onChange={(_, { value: next }) => selectStrategy(next)}
      />
      {selectedStrategy === 'quality' && (
        <div className='key-control-help-v107'>
          <span className='key-source-note-v107'>
            {zh
              ? '质量优先，不作为准入条件'
              : 'Quality preferred, not required'}
          </span>
          <KeyHelpV107
            label={zh ? '未确认能力说明' : 'Unconfirmed capability help'}
            content={
              zh
                ? '优先选择已确认全参、无压缩的 Source，其他有权限且支持本次请求的 Source 仍参与候选。未确认不等于已支持；此偏好不保证输出质量，也不会自动启用渠道或扩大权限。'
                : 'Sources with confirmed full parameters and uncompressed support rank first. Other authorized Sources supporting this request remain candidates. Unconfirmed does not mean confirmed support. This preference is not a quality guarantee and never enables channels or grants access.'
            }
          />
        </div>
      )}
      {selectedStrategy === 'manual' && (
        <Form.Field>
          <label>{zh ? '指定 Source' : 'Specified Source'}</label>
          <Dropdown
            fluid
            multiple
            search
            selection
            options={[
              ...(options.sources || []).map((source) => ({
                key: source.id,
                value: source.id,
                text: `${source.name}${
                  source.dynamic
                    ? zh
                      ? ' · 实际消费回查'
                      : ' · receipt-resolved'
                    : source.multipliers?.length
                    ? ` · x${source.multipliers.join(' / x')}`
                    : ''
                }`,
              })),
              ...sourceSelection.missing.map((ref, index) => ({
                key: sourceIdentityV1019(ref),
                value: sourceIdentityV1019(ref),
                text: `${zh ? '已保存的 Source' : 'Saved Source'} ${index + 1} · ${
                  zh ? '暂不可用' : 'Unavailable'
                }`,
                disabled: true,
              })),
            ]}
            value={sourceSelection.selected}
            onChange={(_, { value: next }) => {
              const selected = (next || [])
                .map((id) =>
                  (options.sources || []).find((source) => source.id === id)
                )
                .filter(Boolean);
              const retained = sourceSelection.missing.filter((ref) =>
                (next || []).includes(sourceIdentityV1019(ref))
              );
              update('source_refs', [
                ...selected.map((source) => ({
                  provider_id: source.provider_id,
                  source_id: source.source_id,
                })),
                ...retained,
              ]);
            }}
          />
          <div className='key-source-note-v107'>
            {zh
              ? '仅显示逻辑 Source 名称和该 Source 在价格表中的模型倍率。Provider 身份用于权限匹配，不显示物理渠道。OpenLux 统一为 discount，实际细分 Source 和倍率由请求后的消费回查确定。'
              : 'Only logical Source names and their model multipliers are shown. Provider identity is used for authorization and physical channels stay hidden. OpenLux is represented by discount; the actual child Source and multiplier come from the post-request receipt.'}
          </div>
        </Form.Field>
      )}
      <KeyRetryPolicyV1021
        policy={policy}
        onChange={(next) => onChange(cleanPolicy(next))}
      />
    </div>
  );
}
